import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Paperclip, Plus, Trash2, X } from 'lucide-react';
import { mirrorAllOnboardingTasks } from '../../utils/onboardingTaskBridge';
import { useOptionalCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import { isEditableTarget } from '../../utils/isEditableTarget';
import { HoverTip } from '../billing/SupplierBillHover';
import { uploadSupplierBillFile } from '../../utils/supplierBillsService';
import {
  SUPPLIER_PAYMENT_TRIGGER_LABELS,
  applyBillTotalEdit,
  applyInstallmentEdit,
  normalizeInstallmentShares,
  resolveInstallmentAmount,
  roundMoney,
  validateSupplierPaymentTerms,
  type SupplierBillCategory,
  type SupplierInstallmentDocument,
  type SupplierInstallmentDocumentKind,
  type SupplierPaymentInstallmentTerm,
  type SupplierPaymentTermsSpec,
  type SupplierPaymentTrigger,
} from '../../utils/supplierBills';
import {
  buildSupplierPaymentTaskOptions,
  formatSupplierPaymentTaskOptionLabel,
} from '../../utils/supplierPaymentTaskOptions';
import './SupplierPaymentTermsModal.css';

let termRowSeq = 0;
function newRowId(): string {
  termRowSeq += 1;
  return `term-${Date.now()}-${termRowSeq}`;
}

function blankRow(percent: number, trigger: SupplierPaymentTrigger): SupplierPaymentInstallmentTerm {
  return { id: newRowId(), percent, trigger };
}

const TRIGGER_ORDER: SupplierPaymentTrigger[] = ['on_approval', 'task_complete', 'fixed_date'];

function formatMoney(n: number): string {
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

const DOCUMENT_KIND_LABELS: Record<SupplierInstallmentDocumentKind, string> = {
  statement: 'Statement',
  invoice: 'Invoice',
  receipt: 'Receipt',
  quote: 'Quote',
  other: 'Other',
};

export function defaultSupplierPaymentTerms(billAmount = 0): SupplierPaymentTermsSpec {
  const bill = Math.round(billAmount);
  const row = { ...blankRow(100, 'on_approval'), label: 'Full payment', amount: bill > 0 ? bill : undefined };
  return {
    installments: bill > 0 ? normalizeInstallmentShares([row], bill) : [row],
  };
}

export type SupplierPaymentTermsEditorProps = {
  billAmount: number;
  projectId?: string;
  value: SupplierPaymentTermsSpec | null;
  onChange: (value: SupplierPaymentTermsSpec | null) => void;
  disabled?: boolean;
  /** Hide the lead paragraph (embedded in upload modal). */
  compact?: boolean;
  /** Timber/CNC bills show a freight-included toggle. */
  billCategory?: SupplierBillCategory;
  /** Installment ids already approved to pay or marked paid. Their dollars stay fixed. */
  lockedInstallmentIds?: string[];
  /** Installment ids already marked paid. These rows show the Already Paid badge. */
  paidInstallmentIds?: string[];
  /** Fired when a final unpaid amount raises or lowers the bill total. */
  onScheduleAmountChange?: (amount: number) => void;
};

const SupplierPaymentTermsEditor: React.FC<SupplierPaymentTermsEditorProps> = ({
  billAmount,
  projectId,
  value,
  onChange,
  disabled = false,
  compact = false,
  billCategory,
  lockedInstallmentIds = [],
  paidInstallmentIds = [],
  onScheduleAmountChange,
}) => {
  const crm = useOptionalCRM();
  const crmProjectTasks = crm?.projectTasks;
  const crmTaskGroups = crm?.taskGroups;
  const crmProjectOnboardings = crm?.projectOnboardings;
  const requestCrmCollections = crm?.requestCrmCollections;
  // Task-anchor options need org-wide PM data (lazy in CRMContext); no-op for suppliers.
  useEffect(() => {
    if (!requestCrmCollections || !projectId) return;
    requestCrmCollections(['projectTasks', 'taskGroups', 'projectOnboardings']);
  }, [requestCrmCollections, projectId]);

  const [rows, setRows] = useState<SupplierPaymentInstallmentTerm[]>([]);
  const [earlyPayDiscount, setEarlyPayDiscount] = useState('');
  const [earlyPayWithin, setEarlyPayWithin] = useState('');
  const [notes, setNotes] = useState('');
  const [includesFreight, setIncludesFreight] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadingRowId, setUploadingRowId] = useState<string | null>(null);
  const [docKindByRow, setDocKindByRow] = useState<Record<string, SupplierInstallmentDocumentKind>>({});
  const [amountDraftByRow, setAmountDraftByRow] = useState<Record<string, string>>({});
  const [percentDraftByRow, setPercentDraftByRow] = useState<Record<string, string>>({});
  const [totalDraft, setTotalDraft] = useState<string | null>(null);
  const [workingBill, setWorkingBill] = useState(billAmount);
  const { user } = useAuth();
  const showFreightToggle = billCategory === 'timber' || billCategory === 'cnc';
  const hoverDocRef = useRef<{ url: string; name: string } | null>(null);
  const [quickLook, setQuickLook] = useState<{ url: string; name: string } | null>(null);
  const quickLookOpenRef = useRef(false);
  useEffect(() => {
    quickLookOpenRef.current = !!quickLook;
  }, [quickLook]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && quickLookOpenRef.current) {
        e.preventDefault();
        e.stopPropagation();
        setQuickLook(null);
        return;
      }
      if (e.code !== 'Space' || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isEditableTarget(e.target)) return;
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'BUTTON') return;
      if (quickLookOpenRef.current) {
        e.preventDefault();
        e.stopPropagation();
        setQuickLook(null);
        return;
      }
      const doc = hoverDocRef.current;
      if (!doc?.url) return;
      e.preventDefault();
      e.stopPropagation();
      setQuickLook(doc);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);
  const lockedIds = useMemo(() => new Set(lockedInstallmentIds), [lockedInstallmentIds]);
  const paidIds = useMemo(() => new Set(paidInstallmentIds), [paidInstallmentIds]);

  useEffect(() => {
    if (value && value.installments.length > 0) {
      setRows(value.installments.map((i) => ({ ...i })));
      setEarlyPayDiscount(value.earlyPayDiscountPercent ? String(value.earlyPayDiscountPercent) : '');
      setEarlyPayWithin(value.earlyPayWithinDays ? String(value.earlyPayWithinDays) : '');
      setNotes(value.notes || '');
      setIncludesFreight(value.includesFreight === true);
    } else {
      const defaults = defaultSupplierPaymentTerms(billAmount);
      setRows(defaults.installments.map((i) => ({ ...i })));
      setEarlyPayDiscount('');
      setEarlyPayWithin('');
      setNotes('');
      setIncludesFreight(false);
    }
    setValidationError(null);
  }, [billAmount, value]);

  useEffect(() => {
    setWorkingBill(billAmount);
    setTotalDraft(null);
  }, [billAmount]);

  const taskOptions = useMemo(() => {
    if (!projectId) return [];
    const groupNameById = new Map(
      (crmTaskGroups ?? [])
        .filter((g) => g.projectId === projectId && g.id)
        .map((g) => [g.id as string, g.name]),
    );
    const allTasks = [
      ...(crmProjectTasks ?? []),
      ...mirrorAllOnboardingTasks(crmProjectOnboardings ?? [], crmTaskGroups ?? []),
    ];
    return buildSupplierPaymentTaskOptions({ projectId, tasks: allTasks, groupNameById });
  }, [projectId, crmProjectTasks, crmTaskGroups, crmProjectOnboardings]);

  const totalPercent = useMemo(() => rows.reduce((s, r) => s + (Number(r.percent) || 0), 0), [rows]);
  const totalAmount = useMemo(
    () => rows.reduce((s, r) => s + resolveInstallmentAmount(r, workingBill), 0),
    [rows, workingBill],
  );
  const usesDollarAmounts = rows.some((r) => r.amount != null && r.amount > 0);

  const emitChange = (
    nextRows: SupplierPaymentInstallmentTerm[],
    nextDiscount: string,
    nextWithin: string,
    nextNotes: string,
    nextIncludesFreight = includesFreight,
    bill = workingBill,
  ) => {
    const shareRows = bill > 0 ? normalizeInstallmentShares(nextRows, bill, lockedIds) : nextRows;
    const discount = Number(nextDiscount);
    const within = Number(nextWithin);
    const terms: SupplierPaymentTermsSpec = {
      installments: shareRows.map((r) => ({
        ...r,
        percent: Number(r.percent) || 0,
        amount: r.amount != null && r.amount > 0 ? roundMoney(r.amount) : undefined,
        netDays: r.netDays && r.netDays > 0 ? Math.round(r.netDays) : undefined,
        documents: r.documents?.length ? r.documents : undefined,
      })),
      earlyPayDiscountPercent: discount > 0 ? discount : undefined,
      earlyPayWithinDays: discount > 0 && within > 0 ? Math.round(within) : undefined,
      notes: nextNotes.trim() ? nextNotes : undefined,
      includesFreight: nextIncludesFreight ? true : undefined,
    };
    const check = validateSupplierPaymentTerms(terms, bill > 0 ? bill : undefined);
    setValidationError(check.ok ? null : check.message);
    onChange(terms);
  };

  const applyPreset = (preset: 'full' | 'half_down' | 'net30') => {
    if (lockedIds.size > 0) {
      setValidationError('Approved or paid installments keep their amount, so the preset was not applied.');
      return;
    }
    const bill = roundMoney(workingBill);
    let nextRows: SupplierPaymentInstallmentTerm[];
    if (preset === 'full') {
      nextRows = [{ ...blankRow(100, 'on_approval'), label: 'Full payment', amount: bill > 0 ? bill : undefined }];
    } else if (preset === 'half_down') {
      const first = bill > 0 ? Math.round(bill / 2) : 0;
      nextRows = [
        { ...blankRow(50, 'on_approval'), label: '50% down', amount: first > 0 ? first : undefined },
        { ...blankRow(50, 'task_complete'), label: '50% at completion' },
      ];
    } else {
      nextRows = [{
        ...blankRow(100, 'task_complete'),
        label: 'Full payment at completion',
        netDays: 30,
        amount: bill > 0 ? bill : undefined,
      }];
    }
    if (bill > 0) nextRows = normalizeInstallmentShares(nextRows, bill);
    setRows(nextRows);
    emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
  };

  const updateRow = (idx: number, patch: Partial<SupplierPaymentInstallmentTerm>) => {
    const nextRows = rows.slice();
    nextRows[idx] = { ...nextRows[idx], ...patch };
    setRows(nextRows);
    emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
  };

  const removeRow = (idx: number) => {
    if (lockedIds.has(rows[idx]?.id)) {
      setValidationError('That installment is already approved or paid, so its amount stays.');
      return;
    }
    let nextRows = rows.filter((_, i) => i !== idx);
    if (workingBill > 0) nextRows = normalizeInstallmentShares(nextRows, workingBill, lockedIds);
    setRows(nextRows);
    emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
  };

  const setRowPercent = (idx: number, percent: number) => {
    if (workingBill > 0) {
      const result = applyInstallmentEdit({
        rows,
        editedIndex: idx,
        billAmount: workingBill,
        mode: 'percent',
        value: percent,
        lockedIds,
      });
      if (!result.ok) {
        setValidationError(lockedIds.has(rows[idx]?.id)
          ? 'That installment is already approved or paid, so its amount stays.'
          : 'That percent would change an approved or paid installment, or leave another at $0. The rows were left as they were.');
        return;
      }
      setRows(result.rows);
      emitChange(result.rows, earlyPayDiscount, earlyPayWithin, notes);
      return;
    }
    updateRow(idx, { percent, amount: undefined });
  };

  const setRowAmount = (idx: number, dollars: number) => {
    if (workingBill > 0) {
      const result = applyInstallmentEdit({
        rows,
        editedIndex: idx,
        billAmount: workingBill,
        mode: 'dollars',
        value: dollars,
        lockedIds,
      });
      if (!result.ok) {
        setValidationError(lockedIds.has(rows[idx]?.id)
          ? 'That installment is already approved or paid, so its amount stays.'
          : 'Already paid numbers never change. Change an unpaid installment or the bill total.');
        return;
      }
      const nextBill = result.billAmount ?? workingBill;
      if (result.billAmount != null && result.billAmount !== workingBill) {
        setWorkingBill(result.billAmount);
        onScheduleAmountChange?.(result.billAmount);
      }
      setRows(result.rows);
      emitChange(result.rows, earlyPayDiscount, earlyPayWithin, notes, includesFreight, nextBill);
      return;
    }
    const amount = Number.isFinite(dollars) && dollars > 0 ? roundMoney(dollars) : 0;
    updateRow(idx, { amount: amount > 0 ? amount : undefined, percent: 0 });
  };

  const commitPercentDraft = (idx: number, rowId: string) => {
    const raw = percentDraftByRow[rowId];
    setPercentDraftByRow((prev) => {
      if (prev[rowId] == null) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    if (raw == null) return;
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '.') return;
    const pct = Number(trimmed);
    if (!Number.isFinite(pct)) return;
    setRowPercent(idx, pct);
  };

  const commitAmountDraft = (idx: number, rowId: string) => {
    const raw = amountDraftByRow[rowId];
    setAmountDraftByRow((prev) => {
      if (prev[rowId] == null) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    if (raw == null) return;
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '.') return;
    const dollars = Number(trimmed);
    if (!Number.isFinite(dollars)) return;
    setRowAmount(idx, dollars);
  };

  const attachDocument = async (idx: number, file: File | null) => {
    const row = rows[idx];
    if (!row || !file) return;
    if (!user?.id) {
      setValidationError('Sign in to attach a file.');
      return;
    }
    const kind = docKindByRow[row.id] || 'statement';
    setUploadingRowId(row.id);
    setValidationError(null);
    try {
      const uploaded = await uploadSupplierBillFile(file, user.id);
      const doc: SupplierInstallmentDocument = {
        id: newRowId(),
        kind,
        fileName: uploaded.fileName,
        storagePath: uploaded.storagePath,
        fileUrl: uploaded.fileUrl,
        contentType: file.type || undefined,
      };
      const nextRows = rows.slice();
      nextRows[idx] = { ...row, documents: [...(row.documents ?? []), doc] };
      setRows(nextRows);
      emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Could not upload that file.');
    } finally {
      setUploadingRowId(null);
    }
  };

  const removeDocument = (idx: number, docId: string) => {
    const row = rows[idx];
    if (!row) return;
    const nextRows = rows.slice();
    nextRows[idx] = {
      ...row,
      documents: (row.documents ?? []).filter((doc) => doc.id !== docId),
    };
    setRows(nextRows);
    emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
  };

  const addRow = () => {
    const remainingPct = Math.max(0, 100 - totalPercent);
    const remainingAmt = workingBill > 0 ? Math.max(0, roundMoney(workingBill) - totalAmount) : 0;
    const added =
      usesDollarAmounts && remainingAmt > 0
        ? {
            ...blankRow(
              workingBill > 0 ? (remainingAmt / workingBill) * 100 : remainingPct || 0,
              'task_complete',
            ),
            amount: remainingAmt,
          }
        : blankRow(remainingPct || 50, 'task_complete');
    const nextRows = [...rows, added].map((r, _, all) => {
      if (all.length > 1 && r.label && /^full payment\b/i.test(r.label)) {
        return { ...r, label: `${Math.round(r.percent)}% installment` };
      }
      return r;
    });
    setRows(nextRows);
    emitChange(nextRows, earlyPayDiscount, earlyPayWithin, notes);
  };

  const canEditTotal = workingBill > 0 && rows.some((row) => !lockedIds.has(row.id));

  const commitTotalDraft = () => {
    const raw = totalDraft;
    setTotalDraft(null);
    if (raw == null) return;
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '.') return;
    const next = Number(trimmed);
    if (!Number.isFinite(next)) return;
    const result = applyBillTotalEdit({
      rows,
      currentBillAmount: workingBill,
      nextBillAmount: next,
      lockedIds,
    });
    if (!result.ok) {
      const paid = rows.reduce(
        (sum, row) => sum + (lockedIds.has(row.id) ? resolveInstallmentAmount(row, workingBill) : 0),
        0,
      );
      setValidationError(
        paid > 0
          ? `Paid installments already total ${formatMoney(paid)}. The bill total has to stay above that, and the extra goes on the last unpaid line.`
          : 'The bill total has to leave the last unpaid installment above $0.',
      );
      return;
    }
    setWorkingBill(result.billAmount);
    onScheduleAmountChange?.(result.billAmount);
    setRows(result.rows);
    emitChange(result.rows, earlyPayDiscount, earlyPayWithin, notes, includesFreight, result.billAmount);
  };

  return (
    <div className={`sptm-editor${compact ? ' sptm-editor--compact' : ''}`}>
      {!compact && workingBill > 0 && (
        <p className="sptm-lead">
          Bill total: <strong>{formatMoney(workingBill)}</strong>. Installments feed the cash flow projection after approval.
        </p>
      )}

      <div className="sptm-presets" role="group" aria-label="Presets">
        <button type="button" className="sptm-preset-btn" disabled={disabled} onClick={() => applyPreset('full')}>
          100% on approval
        </button>
        <button type="button" className="sptm-preset-btn" disabled={disabled} onClick={() => applyPreset('half_down')}>
          50% down / 50% at completion
        </button>
        <button type="button" className="sptm-preset-btn" disabled={disabled} onClick={() => applyPreset('net30')}>
          100% at completion, net 30
        </button>
      </div>

      <div className="sptm-rows">
        {rows.map((row, idx) => {
          const rowPaid = paidIds.has(row.id);
          const rowLocked = rowPaid || lockedIds.has(row.id);
          return (
          <div key={row.id} className={`sptm-row-wrap${rowPaid ? ' sptm-row-wrap--paid' : ''}`}>
            <span className="sptm-num">{idx + 1}</span>
            <div className={`sptm-row${rowLocked ? ' sptm-row--locked' : ''}${rowPaid ? ' sptm-row--paid' : ''}`} role="group" aria-label={`Payment ${idx + 1}`}>
            {rowPaid ? (
              <span className="sptm-paid-badge">Already Paid</span>
            ) : rowLocked ? (
              <span className="sptm-locked-note">Approved — amount stays</span>
            ) : null}
            <div className="sptm-row-fields">
            <div className="sptm-field sptm-field-pct">
              <label htmlFor={`sptm-pct-${row.id}`}>%</label>
              <input
                id={`sptm-pct-${row.id}`}
                type="text"
                inputMode="decimal"
                disabled={disabled || rowLocked}
                aria-label="Installment percent of bill"
                value={
                  percentDraftByRow[row.id] != null
                    ? percentDraftByRow[row.id]
                    : row.percent
                      ? String(Math.round(row.percent * 100) / 100)
                      : ''
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                  setPercentDraftByRow((prev) => ({ ...prev, [row.id]: raw }));
                }}
                onBlur={() => commitPercentDraft(idx, row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitPercentDraft(idx, row.id);
                  }
                }}
              />
            </div>
            <div className="sptm-field sptm-field-amount">
              <label htmlFor={`sptm-amt-${row.id}`}>$</label>
              <input
                id={`sptm-amt-${row.id}`}
                type="text"
                inputMode="decimal"
                disabled={disabled || rowLocked}
                aria-label="Installment amount in dollars"
                value={
                  amountDraftByRow[row.id] != null
                    ? amountDraftByRow[row.id]
                    : row.amount != null && row.amount > 0
                      ? String(row.amount)
                      : workingBill > 0
                        ? String(resolveInstallmentAmount(row, workingBill) || '')
                        : ''
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                  setAmountDraftByRow((prev) => ({ ...prev, [row.id]: raw }));
                }}
                onBlur={() => commitAmountDraft(idx, row.id)}
              />
            </div>
            <div className="sptm-field sptm-field-trigger">
              <label htmlFor={`sptm-trigger-${row.id}`}>Due</label>
              <select
                id={`sptm-trigger-${row.id}`}
                disabled={disabled || rowLocked}
                value={row.trigger}
                onChange={(e) =>
                  updateRow(idx, {
                    trigger: e.target.value as SupplierPaymentTrigger,
                    taskId: undefined,
                    taskTitle: undefined,
                    fixedDate: undefined,
                  })
                }
              >
                {TRIGGER_ORDER.map((t) => (
                  <option key={t} value={t}>
                    {SUPPLIER_PAYMENT_TRIGGER_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            {row.trigger === 'task_complete' && (
              <div className="sptm-field sptm-field-task">
                <label htmlFor={`sptm-task-${row.id}`}>Task</label>
                <select
                  id={`sptm-task-${row.id}`}
                  disabled={disabled || rowLocked}
                  value={row.taskId || ''}
                  onChange={(e) => {
                    const task = taskOptions.find((t) => t.id === e.target.value);
                    updateRow(idx, { taskId: task?.id, taskTitle: task?.title });
                  }}
                >
                  <option value="">
                    {taskOptions.length ? 'Pick a task…' : 'No project tasks found'}
                  </option>
                  {taskOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {formatSupplierPaymentTaskOptionLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {row.trigger === 'fixed_date' && (
              <div className="sptm-field sptm-field-date">
                <label htmlFor={`sptm-date-${row.id}`}>Date</label>
                <input
                  id={`sptm-date-${row.id}`}
                  type="date"
                  disabled={disabled || rowLocked}
                  value={row.fixedDate ? row.fixedDate.toISOString().slice(0, 10) : ''}
                  onChange={(e) =>
                    updateRow(idx, {
                      fixedDate: e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined,
                    })
                  }
                />
              </div>
            )}

            {row.trigger !== 'fixed_date' && (
              <div className="sptm-field sptm-field-net">
                <label htmlFor={`sptm-net-${row.id}`}>Net days</label>
                <input
                  id={`sptm-net-${row.id}`}
                  type="number"
                  min={0}
                  max={120}
                  placeholder="0"
                  disabled={disabled || rowLocked}
                  value={row.netDays ?? ''}
                  onChange={(e) =>
                    updateRow(idx, { netDays: e.target.value ? Number(e.target.value) : undefined })
                  }
                />
              </div>
            )}

            <button
              type="button"
              className="sptm-remove-btn"
              aria-label={`Remove installment ${idx + 1}`}
              disabled={disabled || rows.length <= 1 || rowLocked}
              onClick={() => removeRow(idx)}
            >
              <Trash2 size={14} aria-hidden />
            </button>
            </div>
            <div className="sptm-row-extra">
              <div className="sptm-docs">
                <label htmlFor={`sptm-kind-${row.id}`}>Attach</label>
                <select
                  id={`sptm-kind-${row.id}`}
                  disabled={disabled || rowLocked || uploadingRowId === row.id}
                  value={docKindByRow[row.id] || 'statement'}
                  onChange={(e) => setDocKindByRow((prev) => ({
                    ...prev,
                    [row.id]: e.target.value as SupplierInstallmentDocumentKind,
                  }))}
                >
                  {(Object.keys(DOCUMENT_KIND_LABELS) as SupplierInstallmentDocumentKind[]).map((kind) => (
                    <option key={kind} value={kind}>{DOCUMENT_KIND_LABELS[kind]}</option>
                  ))}
                </select>
                <label className="sptm-file-btn">
                  <Paperclip size={14} aria-hidden />
                  {uploadingRowId === row.id ? 'Uploading…' : 'PDF or image'}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    disabled={disabled || rowLocked || uploadingRowId === row.id}
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      e.target.value = '';
                      void attachDocument(idx, file);
                    }}
                  />
                </label>
                {(row.documents ?? []).map((doc) => (
                  <span key={doc.id} className="sptm-doc">
                    <HoverTip
                      inModal
                      tip={<span className="bp-tip-hint">Press <kbd>Space</kbd> to preview</span>}
                    >
                      <a
                        className="sptm-doc-link"
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        onMouseEnter={() => {
                          hoverDocRef.current = { url: doc.fileUrl, name: doc.fileName };
                        }}
                        onMouseLeave={() => {
                          if (hoverDocRef.current?.url === doc.fileUrl) hoverDocRef.current = null;
                        }}
                      >
                        {DOCUMENT_KIND_LABELS[doc.kind]} — {doc.fileName}
                      </a>
                    </HoverTip>
                    <button
                      type="button"
                      aria-label={`Remove ${doc.fileName}`}
                      disabled={disabled || rowLocked}
                      onClick={() => removeDocument(idx, doc.id)}
                    >
                      <Trash2 size={12} aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            </div>
          </div>
          );
        })}
      </div>

      <div className="sptm-rows-footer">
        <button type="button" className="sptm-add-btn" disabled={disabled} onClick={addRow}>
          <Plus size={14} aria-hidden /> Add installment
        </button>
        {workingBill > 0 ? (
          <label
            className={`sptm-bill-total${
              Math.abs(roundMoney(totalAmount) - roundMoney(workingBill)) > 0.009 || Math.abs(totalPercent - 100) > 0.5
                ? ' sptm-total--warn'
                : ''
            }`}
          >
            Bill total
            <input
              aria-label="Bill total"
              inputMode="decimal"
              disabled={disabled || !canEditTotal}
              value={totalDraft != null ? totalDraft : String(roundMoney(workingBill))}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== '' && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                setTotalDraft(raw);
              }}
              onBlur={commitTotalDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTotalDraft();
                }
              }}
            />
            <span>{totalPercent.toFixed(0)}%</span>
          </label>
        ) : (
          <span className={`sptm-total${Math.abs(totalPercent - 100) > 0.5 ? ' sptm-total--warn' : ''}`}>
            {`Total: ${totalPercent.toFixed(0)}%`}
          </span>
        )}
      </div>
      {workingBill > 0 ? (
        <p className="sptm-total-hint">
          Changing an installment or the bill total updates the other open numbers so they still add up. Already paid numbers never change.
        </p>
      ) : null}

      <section className="sptm-discount">
        <h4 className="sptm-section-label">Early payment discount (optional)</h4>
        <div className="sptm-discount-row">
          <div className="sptm-field">
            <label htmlFor="sptm-discount-pct">Discount %</label>
            <input
              id="sptm-discount-pct"
              type="number"
              min={0}
              max={20}
              step={0.5}
              placeholder="e.g. 1"
              disabled={disabled}
              value={earlyPayDiscount}
              onChange={(e) => {
                setEarlyPayDiscount(e.target.value);
                emitChange(rows, e.target.value, earlyPayWithin, notes);
              }}
            />
          </div>
          <div className="sptm-field">
            <label htmlFor="sptm-discount-days">If paid within (days)</label>
            <input
              id="sptm-discount-days"
              type="number"
              min={0}
              max={60}
              placeholder="e.g. 10"
              disabled={disabled}
              value={earlyPayWithin}
              onChange={(e) => {
                setEarlyPayWithin(e.target.value);
                emitChange(rows, earlyPayDiscount, e.target.value, notes);
              }}
            />
          </div>
        </div>
      </section>

      {showFreightToggle && (
        <label className="sptm-freight-toggle">
          <input
            type="checkbox"
            disabled={disabled}
            checked={includesFreight}
            onChange={(e) => {
              setIncludesFreight(e.target.checked);
              emitChange(rows, earlyPayDiscount, earlyPayWithin, notes, e.target.checked);
            }}
          />
          <span>
            {billCategory === 'cnc'
              ? 'This quote includes outbound freight'
              : 'This quote includes inbound freight'}
            <span className="sptm-freight-hint">
              {billCategory === 'cnc'
                ? 'Drops leftover outbound freight off the cash forecast for this job.'
                : 'Drops leftover inbound freight off the cash forecast for this job.'}
            </span>
          </span>
        </label>
      )}

      <div className="sptm-field sptm-field-notes">
        <label htmlFor="sptm-notes">Notes</label>
        <textarea
          id="sptm-notes"
          rows={2}
          placeholder="Anything else about how this supplier bills…"
          disabled={disabled}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            emitChange(rows, earlyPayDiscount, earlyPayWithin, e.target.value);
          }}
        />
      </div>

      {validationError && (
        <p className="sptm-error" role="alert">
          {validationError}
        </p>
      )}
      {quickLook && createPortal(
        <div
          className="bp-quicklook-overlay sptm-quicklook-overlay"
          onClick={() => setQuickLook(null)}
        >
          <div className="bp-quicklook" onClick={(e) => e.stopPropagation()}>
            <header className="bp-quicklook-head">
              <span className="bp-quicklook-name" title={quickLook.name}>
                {quickLook.name}
              </span>
              <span className="bp-quicklook-hint">Space or Esc to close</span>
              <a
                className="bp-quicklook-open"
                href={quickLook.url}
                target="_blank"
                rel="noreferrer"
                title="Open in a new tab"
              >
                <ExternalLink size={14} />
              </a>
              <button
                type="button"
                className="bp-quicklook-close"
                onClick={() => setQuickLook(null)}
                aria-label="Close preview"
              >
                <X size={16} />
              </button>
            </header>
            <iframe className="bp-quicklook-frame" src={quickLook.url} title={quickLook.name} />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SupplierPaymentTermsEditor;
